import React, { useEffect, useState, useCallback, KeyboardEvent } from "react"
import { Button, Menu, MenuItem, Popover } from "@blueprintjs/core"
import css from "../../ChatInput.module.css"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import type aiEn from "@/lib/i18n/locales/en/ai.json"

// The prompt is what lands in the composer and goes to the agent, so it stays
// as it is; only the label the menu shows is translated.
type Labels = typeof aiEn.promptSuggestions

export type TopicPrompt = {
  label: `ai.promptSuggestions.prompts.${keyof Labels["prompts"]}`
  prompt: string
}

export type Topic = {
  id: string
  label: `ai.promptSuggestions.topics.${keyof Labels["topics"]}`
  prompts: TopicPrompt[]
}

const topics: Topic[] = [
  {
    id: "summary",
    label: "ai.promptSuggestions.topics.summary",
    prompts: [
      {
        label: "ai.promptSuggestions.prompts.summaryGpsNow",
        prompt: "Podsumuj trasy kierowców z GPS teraz",
      },
      {
        label: "ai.promptSuggestions.prompts.summaryYesterday",
        prompt: "Podsumuj przejazdy kierowców z wczoraj",
      },
      {
        label: "ai.promptSuggestions.prompts.summaryTwoWeeks",
        prompt: "Podsumuj trasy kierowców z 2 tygodni",
      },
    ],
  },
  {
    id: "docs",
    label: "ai.promptSuggestions.topics.docs",
    prompts: [
      {
        label: "ai.promptSuggestions.prompts.docsCmrLastDay",
        prompt: "Wyswietl wszystkie CMR z ostatniego dnia",
      },
      {
        label: "ai.promptSuggestions.prompts.docsSignedLastWeek",
        prompt: "Wyświetl wszystkie podpisane CRM z ostatniego tygodnia",
      },
    ],
  },
  {
    id: "drivers",
    label: "ai.promptSuggestions.topics.drivers",
    prompts: [
      {
        label: "ai.promptSuggestions.prompts.driversSummary",
        prompt: "Podsumuj kierowców",
      },
      {
        label: "ai.promptSuggestions.prompts.driversActive",
        prompt: "Lista aktywnych kierowców",
      },
      {
        label: "ai.promptSuggestions.prompts.driversStats",
        prompt: "Statystyki kierowców",
      },
    ],
  },
]

const usePromptSuggestions = (
  inputTextRef: React.RefObject<HTMLTextAreaElement>,
  setValue: (value: string) => void,
  setAutocompleteOpen: (isOpen: boolean) => void,
) => {
  const { t } = useTranslation()
  const [isPromptAutocompleteOpen, setPromptAutocompleteOpen] =
    useState<boolean>(false)
  const [
    promptAutocompleteActiveItemIndex,
    setPromptAutocompleteActiveItemIndex,
  ] = useState<number>(0)
  const [activeTopic, setActiveTopic] = useState<Topic | null>(null)

  const handleTopicClick = useCallback(
    (topic: Topic) => {
      setAutocompleteOpen(false)
      setActiveTopic(topic)
      setPromptAutocompleteActiveItemIndex(0)
      setPromptAutocompleteOpen(true)
      inputTextRef.current?.focus()
    },
    [
      inputTextRef,
      setPromptAutocompleteOpen,
      setActiveTopic,
      setPromptAutocompleteActiveItemIndex,
      setAutocompleteOpen,
    ],
  )

  const renderTopicAutocompleteContent = useCallback(() => {
    if (!isPromptAutocompleteOpen) return undefined

    return (
      <Menu className={css.autocompleteMenu}>
        {activeTopic?.prompts.map((suggestion, index) => (
          <MenuItem
            className={classNames(css.item)}
            key={index}
            active={index === promptAutocompleteActiveItemIndex}
            text={t(suggestion.label)}
            onClick={() => {
              setValue(suggestion.prompt)
              inputTextRef.current?.focus()
              setPromptAutocompleteOpen(false)
            }}
          />
        ))}
      </Menu>
    )
  }, [
    activeTopic,
    setValue,
    inputTextRef,
    setPromptAutocompleteOpen,
    isPromptAutocompleteOpen,
    promptAutocompleteActiveItemIndex,
    t,
  ])

  const promptSuggestionsPopoverRenderer = useCallback(
    () => (
      <Popover
        popoverClassName={css.autocomplete}
        content={renderTopicAutocompleteContent()}
        isOpen={isPromptAutocompleteOpen}
        autoFocus={false}
        enforceFocus={false}
        fill={true}
        matchTargetWidth={true}
        minimal={true}
        usePortal={false}
        placement="top"
      >
        <div className="h-px self-stretch"></div>
      </Popover>
    ),
    [renderTopicAutocompleteContent, isPromptAutocompleteOpen],
  )

  const topicButtonsRenderer = useCallback(
    () => (
      <>
        {topics.map((topic) => (
          <Button
            key={topic.id}
            text={t(topic.label)}
            onClick={() => handleTopicClick(topic)}
          />
        ))}
      </>
    ),
    [handleTopicClick, t],
  )

  const handleOnKeyDownCapture = useCallback(
    (e: KeyboardEvent) => {
      if (
        (e.key === "Tab" || e.key === "Enter") &&
        isPromptAutocompleteOpen &&
        activeTopic
      ) {
        e.preventDefault()
        e.stopPropagation()
        setValue(activeTopic.prompts[promptAutocompleteActiveItemIndex].prompt)
        setPromptAutocompleteOpen(false)
      }

      if (e.key === "Escape" && isPromptAutocompleteOpen) {
        e.preventDefault()
        e.stopPropagation()
        setPromptAutocompleteOpen(false)
      }

      if (e.key === "ArrowDown" && isPromptAutocompleteOpen) {
        e.preventDefault()
        e.stopPropagation()

        if (activeTopic) {
          if (
            promptAutocompleteActiveItemIndex ===
            activeTopic.prompts.length - 1
          ) {
            setPromptAutocompleteActiveItemIndex(0)
          } else {
            setPromptAutocompleteActiveItemIndex(
              promptAutocompleteActiveItemIndex + 1,
            )
          }
        }
      }

      if (e.key === "ArrowUp" && isPromptAutocompleteOpen) {
        e.preventDefault()
        e.stopPropagation()

        if (activeTopic) {
          if (promptAutocompleteActiveItemIndex === 0) {
            setPromptAutocompleteActiveItemIndex(activeTopic.prompts.length - 1)
          } else {
            setPromptAutocompleteActiveItemIndex(
              promptAutocompleteActiveItemIndex - 1,
            )
          }
        }
      }
    },
    [
      activeTopic,
      setValue,
      isPromptAutocompleteOpen,
      setPromptAutocompleteActiveItemIndex,
      promptAutocompleteActiveItemIndex,
    ],
  ) as unknown as EventListener

  useEffect(() => {
    const textInputElement = inputTextRef.current
    textInputElement?.addEventListener("keydown", handleOnKeyDownCapture, {
      capture: true,
    })
    return () => {
      textInputElement?.removeEventListener("keydown", handleOnKeyDownCapture, {
        capture: true,
      })
    }
  }, [inputTextRef, handleOnKeyDownCapture])

  return {
    isPromptAutocompleteOpen,
    promptSuggestionsPopoverRenderer,
    topicButtonsRenderer,
  }
}

export default usePromptSuggestions
